/**
 * Date helpers for business dates.
 *
 * A business date (fecha de fondeo, vencimiento, gasto, ...) is a "date key":
 * a `YYYY-MM-DD` string. The backend stores date-only fields as UTC midnight
 * and returns them as `YYYY-MM-DD` or `YYYY-MM-DDT00:00:00.000Z`, so the
 * calendar day is always the first 10 characters.
 *
 * Never parse a date key with `new Date(str)` and then read it back with local
 * getters or `toLocaleDateString` without a time zone: in UTC-4 that shows the
 * previous day. Use the helpers here instead.
 *
 * "Today" is always the business day in America/Caracas, regardless of the
 * browser time zone.
 */

export const BUSINESS_TIME_ZONE = "America/Caracas";

// Caracas is UTC-4 all year (no DST).
const BUSINESS_UTC_OFFSET = "-04:00";

const DATE_KEY_REGEX = /^(\d{4})-(\d{2})-(\d{2})/;

const pad = (value: number) => String(value).padStart(2, "0");

const parseKey = (key: string): [number, number, number] | null => {
  const match = DATE_KEY_REGEX.exec(key);
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};

const keyFromUtcDate = (date: Date) =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )}`;

/** Today's date key in the business time zone (America/Caracas). */
export const getTodayKey = (now: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: BUSINESS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const getPart = (type: string) =>
    parts.find((part) => part.type === type)?.value || "";

  return `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
};

/**
 * Calendar day of a backend date (`YYYY-MM-DD` or UTC-midnight ISO string) as
 * a date key. Returns "" for empty or unparseable values.
 */
export const toDateKey = (value?: string | null): string => {
  if (!value) return "";
  const parsed = parseKey(value);
  return parsed ? value.slice(0, 10) : "";
};

/** `dd/mm/yyyy` for a backend date. Returns "-" for empty values. */
export const formatDateKey = (value?: string | null): string => {
  const key = toDateKey(value);
  if (!key) return "-";
  const [year, month, day] = key.split("-");
  return `${day}/${month}/${year}`;
};

/** Date key from a `Date` using its local calendar fields (e.g. flatpickr). */
export const localDateToKey = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Local-midnight `Date` for a date key (for pickers, never for display). */
export const keyToLocalDate = (key: string): Date => {
  const parsed = parseKey(key);
  if (!parsed) return new Date(NaN);
  return new Date(parsed[0], parsed[1] - 1, parsed[2]);
};

/** Adds (or subtracts) whole days to a date key. */
export const shiftDateKey = (key: string, days: number): string => {
  const parsed = parseKey(key);
  if (!parsed) return key;
  return keyFromUtcDate(
    new Date(Date.UTC(parsed[0], parsed[1] - 1, parsed[2] + days))
  );
};

/** Adds (or subtracts) months, clamping to the last day of the month. */
export const addMonthsToKey = (key: string, months: number): string => {
  const parsed = parseKey(key);
  if (!parsed) return key;
  const [year, month, day] = parsed;
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  return keyFromUtcDate(
    new Date(
      Date.UTC(
        target.getUTCFullYear(),
        target.getUTCMonth(),
        Math.min(day, lastDay)
      )
    )
  );
};

/** Date key of the first day of the month that contains `key`. */
export const startOfMonthKey = (key: string): string => `${key.slice(0, 7)}-01`;

/** Date key of the first day of the year that contains `key`. */
export const startOfYearKey = (key: string): string => `${key.slice(0, 4)}-01-01`;

/** Date key of the Monday of the week that contains `key`. */
export const startOfWeekKey = (key: string): string => {
  const parsed = parseKey(key);
  if (!parsed) return key;
  const weekday = new Date(
    Date.UTC(parsed[0], parsed[1] - 1, parsed[2])
  ).getUTCDay();
  return shiftDateKey(key, -((weekday + 6) % 7));
};

/** True if the backend date is strictly before today (Caracas). */
export const isBeforeToday = (value?: string | null): boolean => {
  const key = toDateKey(value);
  return Boolean(key) && key < getTodayKey();
};

/**
 * Inclusive ISO bounds of the business days `fromKey`..`toKey`, to filter
 * timestamps by Caracas day.
 */
export const dayRangeToIso = (
  fromKey?: string,
  toKey?: string
): { from?: string; to?: string } => {
  const from = fromKey
    ? new Date(`${toDateKey(fromKey)}T00:00:00.000${BUSINESS_UTC_OFFSET}`)
    : undefined;
  const to = toKey
    ? new Date(`${toDateKey(toKey)}T23:59:59.999${BUSINESS_UTC_OFFSET}`)
    : undefined;

  return {
    from: from && !Number.isNaN(from.getTime()) ? from.toISOString() : undefined,
    to: to && !Number.isNaN(to.getTime()) ? to.toISOString() : undefined,
  };
};

const instantParts = (value: string, withTime: boolean) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(withTime
      ? { hour: "2-digit", minute: "2-digit", hour12: true }
      : {}),
  }).formatToParts(date);
  const getPart = (type: string) =>
    parts.find((part) => part.type === type)?.value || "";
  return getPart;
};

/** `dd/mm/yyyy` of a real timestamp, in the business time zone. */
export const formatInstantDate = (value?: string | null): string => {
  if (!value) return "-";
  const getPart = instantParts(value, false);
  if (!getPart) return "-";
  return `${getPart("day")}/${getPart("month")}/${getPart("year")}`;
};

/** `dd/mm/yyyy hh:mm AM/PM` of a real timestamp, in the business time zone. */
export const formatInstantDateTime = (value?: string | null): string => {
  if (!value) return "-";
  const getPart = instantParts(value, true);
  if (!getPart) return "-";
  return `${getPart("day")}/${getPart("month")}/${getPart("year")} ${getPart(
    "hour"
  )}:${getPart("minute")} ${getPart("dayPeriod").toUpperCase()}`;
};
